/**
 * Utility functions for currency handling with user preferences
 */

import { useAuthStore } from '../stores';
import { formatCurrency as baseFormatCurrency } from './formatters';

// Currency information interface
interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  decimals: number;
}

// Common currencies
export const COMMON_CURRENCIES: Record<string, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', decimals: 2 },
  TND: { code: 'TND', symbol: 'TND', name: 'Tunisian Dinar', decimals: 2 }
  };

/**
 * Format amount using user's preferred currency
 */
export async function formatCurrencyWithUserPreference(
  amount: number, 
  locale: string = 'en-US'
): Promise<string> {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }
  return baseFormatCurrency(amount, getUserCurrency(), locale);
}

/**
 * Get user's preferred currency from store
 */
export function getUserCurrency(): string {
  const authStore = useAuthStore();
  return authStore.user?.currency || 'EUR';
}

/**
 * Convert amount from one currency to another (requires exchange rates)
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  exchangeRates: Record<string, number>
): number {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  const fromRate = exchangeRates[fromCurrency];
  const toRate = exchangeRates[toCurrency];

  if (!fromRate || !toRate) {
    throw new Error(`Exchange rate not available for ${fromCurrency} to ${toCurrency}`);
  }

  // Convert to base currency (usually USD) then to target currency
  const baseAmount = amount / fromRate;
  return baseAmount * toRate;
}

/**
 * Format currency with explicit currency code
 */
export function formatCurrencyExplicit(
  amount: number,
  currencyCode: string,
  locale: string = 'en-US',
  options: Intl.NumberFormatOptions = {}
): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }

  const currency = COMMON_CURRENCIES[currencyCode];
  if (!currency) {
    throw new Error(`Unsupported currency: ${currencyCode}`);
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: currency.decimals,
    maximumFractionDigits: currency.decimals,
    ...options
  }).format(amount);
}

/**
 * Parse currency string to number
 */
export function parseCurrencyString(currencyString: string): number | null {
  if (!currencyString || typeof currencyString !== 'string') {
    return null;
  }

  // Remove currency symbols and spaces
  const cleanString = currencyString
    .replace(/[^\d.,\-]/g, '')
    .replace(/,/g, '.');

  const parsed = parseFloat(cleanString);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Get currency symbol for a given currency code
 */
export function getCurrencySymbol(currencyCode: string): string {
  const currency = COMMON_CURRENCIES[currencyCode];
  return currency?.symbol || currencyCode;
}

/**
 * Get currency info
 */
export function getCurrencyInfo(currencyCode: string): CurrencyInfo | null {
  return COMMON_CURRENCIES[currencyCode] || null;
}

/**
 * Validate currency code
 */
export function isValidCurrencyCode(currencyCode: string): boolean {
  return currencyCode in COMMON_CURRENCIES;
}

/**
 * Format amount with thousand separators but no currency symbol
 */
export function formatAmount(
  amount: number,
  decimals: number = 2,
  locale: string = 'en-US'
): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount);
}

/**
 * Round currency amount to appropriate decimal places
 */
export function roundCurrency(amount: number, currencyCode: string): number {
  const currency = COMMON_CURRENCIES[currencyCode];
  const decimals = currency?.decimals ?? 2;
  
  const factor = Math.pow(10, decimals);
  return Math.round(amount * factor) / factor;
}

export type { CurrencyInfo };
